import type { DownloadedFile } from '@/lib/api-client';
import { saveBlob } from '@/lib/save-blob';
import {
  downloadInstrumentRunProteoformsCsv,
  downloadInstrumentRunsCsv,
  type InstrumentRunsDownloadOptions,
} from '@/services/instrumentRuns';
import { downloadOrgsCsv, type OrgsDownloadOptions } from '@/services/organizations';
import {
  downloadOrgSampleCsv,
  downloadOrgSamplesCsv,
  downloadProteoformSampleSummariesCsv,
  downloadSampleProteoformsCsv,
  downloadStudyProteoformsCsv,
  downloadStudySamplesCsv,
  type OrgSamplesDownloadOptions,
  type ProteoformSampleSummariesDownloadOptions,
  type SampleProteoformsDownloadOptions,
  type StudyProteoformsDownloadOptions,
  type StudySamplesDownloadOptions,
} from '@/services/samples';
import { downloadOrgStudiesCsv, type StudiesDownloadOptions } from '@/services/studies';
import { downloadOrgUsersCsv, downloadUsersCsv, type UsersDownloadOptions } from '@/services/users';
import { useMutation } from '@tanstack/react-query';

// A mutation, not a query: downloads are user-triggered and must never be cached or refetched.
function useFileDownload<TVars>(fetcher: (vars: TVars) => Promise<DownloadedFile>, fallbackFilename: string) {
  return useMutation<string, Error, TVars>({
    mutationFn: async vars => {
      const { blob, filename } = await fetcher(vars);
      const resolved = filename ?? fallbackFilename;
      saveBlob(blob, resolved);
      return resolved;
    },
  });
}

/** CSV export of a study's samples, honouring the current sort and filters. */
export function useStudySamplesCsvDownload(orgId: string, studyId: string) {
  return useFileDownload<StudySamplesDownloadOptions | undefined>(
    options => downloadStudySamplesCsv(orgId, studyId, options),
    `study_samples_${studyId}.csv`,
  );
}

/** CSV export of a run's proteoform rows — the Run Details Sample Details grid. */
export function useInstrumentRunProteoformsCsvDownload(orgId: string, runId: string) {
  return useFileDownload<SampleProteoformsDownloadOptions>(
    options => downloadInstrumentRunProteoformsCsv(orgId, runId, options),
    `run_proteoforms_${runId}.csv`,
  );
}

/** CSV export of an org's instrument runs, honouring the current sort and filters. */
export function useInstrumentRunsCsvDownload(orgId: string) {
  return useFileDownload<InstrumentRunsDownloadOptions | undefined>(
    options => downloadInstrumentRunsCsv(orgId, options),
    `instrument_runs_${orgId}.csv`,
  );
}

/** CSV export of an org's study list, honouring the current sort and filters. */
export function useOrgStudiesCsvDownload(orgId: string) {
  return useFileDownload<StudiesDownloadOptions | undefined>(
    options => downloadOrgStudiesCsv(orgId, options),
    `studies_${orgId}.csv`,
  );
}

/** CSV export of an org's full (cross-study) sample list, honouring the current sort and filters. */
export function useOrgSamplesCsvDownload(orgId: string) {
  return useFileDownload<OrgSamplesDownloadOptions | undefined>(
    options => downloadOrgSamplesCsv(orgId, options),
    `samples_${orgId}.csv`,
  );
}

/** CSV export of one sample's full detail — the Sample Details page's own download. */
export function useOrgSampleCsvDownload(orgId: string, sampleId: string) {
  return useFileDownload<void>(() => downloadOrgSampleCsv(orgId, sampleId), `sample_${sampleId}.csv`);
}

/** CSV export of one sample's proteoform rows, honouring the current sort/threshold/filters. */
export function useSampleProteoformsCsvDownload(orgId: string, sampleId: string) {
  return useFileDownload<SampleProteoformsDownloadOptions | undefined>(
    options => downloadSampleProteoformsCsv(orgId, sampleId, options),
    `proteoform_abundances_${sampleId}.csv`,
  );
}

/** CSV export of a study's proteoform rows for one assay — the Tau Samples detail table. */
export function useStudyProteoformsCsvDownload(orgId: string, studyId: string) {
  return useFileDownload<StudyProteoformsDownloadOptions>(
    options => downloadStudyProteoformsCsv(orgId, studyId, options),
    `study_proteoforms_${studyId}.csv`,
  );
}

/** CSV export of a study's per-sample proteoform summary — the Study Detail assay table. */
export function useProteoformSampleSummariesCsvDownload(orgId: string, studyId: string) {
  return useFileDownload<ProteoformSampleSummariesDownloadOptions>(
    options => downloadProteoformSampleSummariesCsv(orgId, studyId, options),
    `study_sample_summary_${studyId}.csv`,
  );
}

/** CSV export of the system-wide user list, honouring the current sort and filters. */
export function useUsersCsvDownload() {
  return useFileDownload<UsersDownloadOptions | undefined>(options => downloadUsersCsv(options), 'users.csv');
}

/** CSV export of an org's user list, honouring the current sort and filters. */
export function useOrgUsersCsvDownload(orgId: string) {
  return useFileDownload<UsersDownloadOptions | undefined>(
    options => downloadOrgUsersCsv(orgId, options),
    `users_${orgId}.csv`,
  );
}

/** CSV export of the organizations list, honouring the current sort and filters. */
export function useOrgsCsvDownload() {
  return useFileDownload<OrgsDownloadOptions | undefined>(options => downloadOrgsCsv(options), 'organizations.csv');
}
